import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { has as _has, includes as _includes } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

import {
  billAccountCanRegisterQuery,
  fromBillAccountCanRegisterActions
} from '@amfam/billing/bill-account-can-register/data-access';
import {
  AddBillAccountFormModel,
  BillAccountActions
} from '@amfam/billing/billaccount/data-access';
import { pendingPaymentActions } from '@amfam/billing/pending-payment/data-access';
import { PendingRegistrationsActions } from '@amfam/billing/registration/data-access';
import { BillAccount, BillingPaymentPaths } from '@amfam/shared/models';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { ValidationService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-bill-account-add',
  templateUrl: './bill-account-add.component.html',
  styleUrls: ['./bill-account-add.component.scss'],
  standalone: false
})
export class BillAccountAddComponent implements OnInit, OnDestroy {
  @Input() newBillingDesign = false;
  addBillAcctForm: UntypedFormGroup;
  billAccountSearchError: boolean;
  disableAddBillAcctBtn = false;
  showForm = false;
  submitted = false;
  ineligibleReasonCode: string;
  findBtnContent: string;
  linkBtn: string;
  private ineligibleReasonsList: Array<string>;

  // Submitted values for filtering state subscription
  private billAccountNumberSubmitted: string;
  private policyNumberSubmittted: string;
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private copyService: CopyService,
    public roleService: ImpersonateRolesService
  ) {
    this.ineligibleReasonsList = this.copyService.getCopy('billing.ineligibleReasonsList');
  }

  ngOnInit() {
    this.findBtnContent = this.copyService.getCopy('shared.find');
    this.linkBtn = this.copyService.getCopy('billing.linkAcctBtn');

    this.addBillAcctForm = this.formBuilder.group({
      accountNumber: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(25),
          ValidationService.accountNumberValidator
        ])
      ],
      policyNumber: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(30),
          ValidationService.policyNumberValidator
        ])
      ]
    });

    this.store
      .select(billAccountCanRegisterQuery.selectBillAccountCanRegisterStateWithBillAccounts)
      .pipe(
        filter(
          state =>
            _has(state.canRegister, 'billAccountNumber') &&
            _has(state.canRegister, 'policyNumber') &&
            state.canRegister.billAccountNumber === this.billAccountNumberSubmitted &&
            // AS: Billing returns and expects the policy number to be sent with checkdigits
            // so instead of matching the complete policy number we check if the user inputted policy number
            // is part of the response.
            _includes(state.canRegister.policyNumber, this.policyNumberSubmittted)
        ),
        takeUntil(this.stop$),
        debounceTime(300)
      )
      .subscribe(state => {
        // Enable / disable button based on loading state.
        this.disableAddBillAcctBtn = state.canRegister.loading;

        // Determine if a billAccount was found.
        if (state.canRegister.loaded) {
          if (state.canRegister.billingAccount) {
            if (state.canRegister.canRegister === true) {
              const matchingBillAccount: BillAccount = state.billAccounts.find(
                billAccount => billAccount.billAccountNumber === state.canRegister.billAccountNumber
              );
              const isAssociatedBillAccount =
                matchingBillAccount && matchingBillAccount.associated === true ? true : false;

              /*
              1. Dispatch load detail action if canRegister === true
              2. Store pending registration if canRegister === true
              3. Clear pending payments if canRegister === true
              4. Re-route if canRegister === true
            */

              const actionPayload = {
                billAccountNumber: state.canRegister.billAccountNumber,
                policyNumber: state.canRegister.policyNumber,
                associated: isAssociatedBillAccount
              };

              // If its already associated we've already run this action
              if (!isAssociatedBillAccount) {
                this.store.dispatch(BillAccountActions.billAccountLoadDetail(actionPayload));
              }
              this.store.dispatch(pendingPaymentActions.ClearPendingPaymentsAction());
              this.store.dispatch(
                PendingRegistrationsActions.PendingRegistrationsAddOne({
                  associated: isAssociatedBillAccount,
                  billAccountNumber: state.canRegister.billAccountNumber,
                  policyNumber: state.canRegister.policyNumber
                })
              );
              this.store.dispatch(
                fromRouterActions.Go({
                  path: [BillingPaymentPaths.BILLING_REGISTRATION]
                })
              );
            } else {
              const code = state.canRegister.billingAccount.eligibleBillingMethods
                .flatMap(method => method.ineligibleReasons)
                .map(reason => reason.name)
                .filter(name => this.ineligibleReasonsList.includes(name));

              if (code && code.length === 1) {
                this.ineligibleReasonCode = code[0];
              }
            }
          } else {
            this.billAccountSearchError = true;
          }
        }
      });

    this.addBillAcctForm.valueChanges.pipe(takeUntil(this.stop$)).subscribe(() => {
      // SK: reset all error flags.
      this.disableAddBillAcctBtn = false;
      this.billAccountSearchError = false;
      this.ineligibleReasonCode = '';
    });

    // SK: force reset error flags, because RegisterCheck eventType has data which we not requested on reloading this component.
    // SK: may be not an ideal solution, hack for now.
    if (!this.addBillAcctForm.valid) {
      this.ineligibleReasonCode = '';
      this.billAccountSearchError = false;
    }
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  showAddBillAcctForm() {
    this.showForm = true;
  }

  verifyBillAccountDetails(formData: AddBillAccountFormModel) {
    this.disableAddBillAcctBtn = true;
    this.billAccountNumberSubmitted = formData.accountNumber.replace(/-/g, '');
    this.policyNumberSubmittted = formData.policyNumber.replace(/-/g, '').toUpperCase();
    const payload = {
      billAccountNumber: this.billAccountNumberSubmitted,
      policyNumber: this.policyNumberSubmittted
    };
    this.store.dispatch(fromBillAccountCanRegisterActions.CanRegisterAction(payload));
  }

  verifyAddBillAccountDetails(formData: AddBillAccountFormModel) {
    this.submitted = true;
    if (this.addBillAcctForm.valid) {
      this.disableAddBillAcctBtn = true;
      this.billAccountNumberSubmitted = formData.accountNumber.replace(/-/g, '');
      this.policyNumberSubmittted = formData.policyNumber.replace(/-/g, '').toUpperCase();
      const payload = {
        billAccountNumber: this.billAccountNumberSubmitted,
        policyNumber: this.policyNumberSubmittted
      };
      this.store.dispatch(fromBillAccountCanRegisterActions.CanRegisterAction(payload));
    }
  }
}
